/* eslint no-console: "off" */
import { AxiosError } from 'axios';
import {
  CategoryDisplayType,
  MiReportSource,
  PublicSystemPermissions,
} from './graphql';
import {
  Case,
  PhoneLineType,
  PublicReportingAccount,
  PublicReportSubmission,
  RelationshipType,
  REPORT_SOURCE,
} from './types';

export const API_URL = '/api';

/**
 * REST API Response Types
 */
export interface RESTResponse<T> {
  data: T;
  status: number;
}

/** ****************** PUBLIC REPORTING *******************/

export const PUBLIC_LOGIN = '/public/sessions';

export const PUBLIC_TOUCH = '/public/sessions/touch';

export const PUBLIC_REPORTS = '/public/reports';

export type PublicReportsResponse = RESTResponse<{
  report: PublicReportSubmission;
  account: PublicReportingAccount;
}>;

export const PUBLIC_MESSAGES = (reportId: string, translate?: boolean) =>
  `/public/messages/${reportId}?translate=${translate}`;

export const PUBLIC_SET_TRANSLATION_ENABLED = (
  userId: number,
  translationEnabled: boolean,
) =>
  `/public/messages/translationEnabled?translation=${translationEnabled}&user=${userId}`;

export const PUBLIC_REPORT = (reportId: string) =>
  `/public/reports/${reportId}`;

export const RELATIONSHIP_TYPES = (slug: string) =>
  `/public/relationship-types/${slug}`;

export const PUBLIC_ORGANISATIONS = '/public/organisations';

export const PUBLIC_ORGANISATION = (organisation: string) =>
  `${PUBLIC_ORGANISATIONS}/${organisation.toLowerCase()}`;

export const PUBLIC_CHALLENGE = (organisation: string) =>
  `/public/challenges/${organisation}`;

export const PUBLIC_SOLUTION = '/public/solutions';

export const OPEN_PASSWORDS = '/public/passwords';

/******************************************************** */

/** ****************** VAULT TALK REPORTING *******************/
export const VAULT_TALK_LOGIN = '/public/sessions?mode=pin';

/******************************************************** */

/**
 * ************ REPORTER PORTAL *****************
 */
export const REPORTING_LOGIN = '/reporting/sessions';

/**
 * ************ PORTAL: OPEN *****************
 */
export const LOGIN = '/portal/sessions';

export const LOGIN_CODE = '/portal/sessions/code';

export const LOGIN_2FA = '/portal/sessions/login-2fa';

export const RESETS = '/portal/resets';

export const PASSWORDS = '/portal/passwords';

export const TOKENS = '/portal/tokens';

export const RESEND_INVITE = '/app/invites/resend';

export const USER_PORTAL_INVITE = '/reporting/activate';

export const AUTHENTICATION_TYPE = '/portal/authenticationType';

/****************************************** */

/** ****************** PORTAL: MANAGER *******************/
export const REPORT = (reportId: string) => `/portal/reports/${reportId}`;

export const MANUAL_INTAKE_REPORT = (id: string) =>
  `/portal/manualIntake/${id}`;

export const MANUAL_INTAKE_REPORTS = '/portal/manualIntake';

export const DELETE_DRAFT = (reportId: string) =>
  `/portal/manualIntake/${reportId}`;

export const ASSIGN_RANDOM_CM = (reportId: string) =>
  `/portal/manualIntake/${reportId}/assignRandomCM`;

export const UPDATE_STATUS = (reportId: string) =>
  `/portal/reports/${reportId}/status`;

export const UPLOAD_EVIDENCE = (reportId: string) =>
  `/api/portal/evidence/${reportId}/upload`;

export const MANAGERS = '/portal/managers';

export const AVAILABILITY = '/portal/availability';
export const CASE_SETTINGS = '/portal/caseSettings';

export const DRAFT_AUDIT = (reportId: string) =>
  `/api/portal/reports/${reportId}/audit`;

export const DRAFT_EVIDENCE = (reportId: string) =>
  `/portal/evidence/${reportId}`;

/* ************************************************* */

/* ***************** PORTAL: ADMIN **************************** */

export const INSIGHTS = '/portal/insights';

export const INVITE = '/portal/invites';

export const LOGO = '/portal/configurations/logo';

export const CONFIGURATIONS = '/portal/configurations';

export const USERS = '/portal/users';

export const USER = (id: string) => `/portal/users/${id}`;

export const UPDATE_USER = (id: string) => `/portal/updateUser/${id}`;

export const EXPIRE = '/portal/users/expire/new';
export const EXPIREOLD = '/portal/users/expire';

export const AUTHENTICATION_ADMIN = '/portal/authenticationAdmin';

export const SUBMISSION_TYPES = (id?: number) =>
  id !== undefined
    ? `/portal/submissionTypes/${id}`
    : '/portal/submissionTypes';

/** *************************************************** */

/* *************** PORTAL: OTHER ************************* */

export const FEATURES = '/portal/features';

export const GLOBAL_FEATURES = '/portal/globalFeatures';

export const NOTIFICATIONS = '/portal/notifications';

export const REPORT_PDF = (id: string) => `/portal/exports/${id}/pdf`;

export const TOUCH = '/portal/sessions/touch';

export const VALIDATE = '/portal/validateEmail';

export const CASES_CSV = '/portal/cases/csv';
export const DELETED_SANITIZED_CASES_CSV =
  '/portal/cases/deleted-sanitized-csv';

/* *************************************************** */

export enum EvidenceType {
  DATE = '@evidence/date',
  DESCRIPTION = '@evidence/description',
  EMOTION = '@evidence/emotion',
  LOCATION = '@evidence/location',
  PERPETRATOR = '@evidence/perpetrator',
  PHOTO = '@evidence/photo',
  TIME = '@evidence/time',
  WITNESS = '@evidence/witness',
  FILE_UPLOAD = '@evidence/file-upload',
}

export enum ReportStatus {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  INVESTIGATING = 'INVESTIGATING',
  CLOSED = 'CLOSED',
  READ = 'READ',
  LODGED = 'LODGED',
}

export enum VisibleReportStatus {
  NEW = 'NEW',
  INVESTIGATING = 'INVESTIGATING',
  CLOSED = 'CLOSED',
  READ = 'READ',
}

export enum ReportCategory {
  HARASSMENT = 'Sexual Harassment',
  THEFTFRAUD = 'Theft/Fraud',
  DISCRIMINATION = 'Discrimination',
  BULLYING = 'Bullying/Harassment',
  OTHER = 'Other',
}

export enum NotificationsType {
  NEW = 'new',
  ASSIGNED = 'assigned',
  REASSIGNED = 'reassigned',
  CASE_REASSIGNED = 'case-reassigned',
  STALE = 'stale',
  MESSAGE = 'message',
  CASE_TEAM_INVITE = 'case-team-invite',
  PERMISSIONS_REMOVED = 'permissions_removed',
  POST_SUBMISSION_FILES_ADDED = 'post_submission_files_added',
  APP_MESSAGE = 'app_message',
  APP_STATUS_CHANGE = 'app_status_change',
  APP_REPORT_REASSIGNED = 'app_report_reassigned',
  APP_MATCH = 'app_match',
  APP_FILE_SCAN_FAILED = 'app_file_scan_failed',
  REPORTER_HUB_MESSAGE = 'reporter_hub_message',
  DATA_SANITISATION_REPORTER_ANONYMISED = 'data_sanitisation_reporter_anonymised',
  DATA_SANITISATION = 'data_sanitisation',
  REPORT_SUBMITTED = 'report_submitted',
  OPR_STATUS_CHANGE = 'opr_status_change',
  OPR_MESSAGE = 'opr_message',
  CASE_LINK_CREATED = 'case_link_created',
  CASE_LINK_DELETED = 'case_link_deleted',
  IMPORT_JOB_COMPLETED_SUCCESSFULLY = 'import_job_completed_successfully',
  IMPORT_JOB_PARTIALLY_SUCCEEDED = 'import_job_partially_succeeded',
  IMPORT_JOB_FAILED = 'import_job_failed',
  MITIGATION_PLAN_REMINDER = 'mitigation_plan_reminder',
}

export enum NotificationState {
  CREATED = 0,
  SEEN = 1,
  READ = 2,
  CLOSED = 3,
}

export enum PermissionsType {
  APP = 'app',
  MANAGER = 'manager',
  ADMIN = 'admin',
  SUPER = 'super',
  PUBLIC = 'public',
  COLLABORATOR = 'collaborator',
  MODERATOR = 'moderator',
}

export enum SystemRolesName {
  MANAGER = 'DEFAULT ROLE manager',
  ADMIN = 'DEFAULT ROLE admin',
  SUPER = 'DEFAULT ROLE super',
  COLLABORATOR = 'CUSTOM ROLE collaborator',
  MODERATOR = 'CUSTOM ROLE moderator',
  REVIEWER = 'CUSTOM ROLE reviewer',
  COI_SUPER = 'CUSTOM ROLE coi_super',
}

export enum FeatureKey {
  PUBLIC_REPORTING = 'public-reporting',
  PDF_EXPORT = 'pdf-export',
  ANONYMOUS = 'anonymous',
}

export enum ReportSubstantiation {
  UNDETERMINED = 'Undetermined',
  UNSUBSTANTIATED = 'Unsubstantiated',
  PARTIALLY_SUBSTANTIATED = 'Partially-substantiated',
  SUBSTANTIATED = 'Substantiated',
}

export type SystemRolesType = {
  id: string;
  name: string;
  label?: string | null;
  publicSystemPermissions?: PublicSystemPermissions | null;
};

export interface UnsealedReport {
  category: string;
  evidence: {
    events: {
      created: string;
      id: string | number;
      report: string;
      updated: string;
    }[];
    evidence: {
      created: string;
      event: string | number;
      id: string | number;
      report: string;
      timestamp: string;
      type: string;
      updated: string;
      value: { id: string; path: string } | string | undefined | Perpetrator;
    }[];
    report?: {
      id: string | number;
      category: string;
    };
  };
  id: string | number;
  perpetrators: { external?: boolean; id?: string; name: string }[];
  report: string;
  status: string;
  User: {
    id: number;
    name: string;
    email: string;
    contactEmail?: string;
    role: string;
    eid: string;
    relationshipToOrganisation?: string;
    relationshipToCompany?: number | null;
  };
  user: string;
  assigneeName: string;
  substantiation?: string;
  miReportSource?: MiReportSource;
  relationshipToCompany?: number | null;
}

export interface UpdateReport extends Partial<UnsealedReport> {
  assignee?: number;
  anonymous?: boolean;
  reportedCategory?: CategoryDisplayType;
  determinedCategory?: CategoryDisplayType;
  cases?: Case[];
  userUpdates?: {
    email?: string;
    name?: string;
    relationshipToCompany?: number;
  };
  substantiation?: ReportSubstantiation;
  miReportSource?: MiReportSource;
  submittedAt?: Date;
  caseAttributes?: {
    readAt?: Date | null;
    investigatingAt?: Date | null;
    closedAt?: Date | null;
  };
}

export interface ReportType {
  relationshipType?: RelationshipType;
  public: boolean;
  anonymous: boolean | null;
  assignee: string | null;
  category: string;
  createdAt: string;
  Cases: Case[];
  id: number;
  assigneeName: string;
  pk?: {
    User: {
      available: boolean;
      createdAt: string;
      department: string;
      eid: string;
      email: string;
      id: number;
      name: string;
      organisation: number;
      permissions: Array<PermissionsType>;
      role: string;
      site: string;
      updatedAt: string;
      contactEmail?: string;
      relationshipToCompany?: number | null;
    };
    createdAt: string;
    id: number;
    pk: string;
    updatedAt: string;
    user: number;
  };
  Reporter?: {
    id: number;
    name: string;
    email?: string;
    contactEmail?: string;
    eid?: string;
    role?: string;
    relationshipToCompany?: number | null;
  };
  report: string;
  sealed: {
    category: ReportCategory;
    evidence: {
      events: Array<{
        created: string;
        id: number;
        report: string;
        updated: string;
      }>;
      evidence: Array<{
        created: string;
        event: number;
        id: number;
        report: string;
        timestamp: string;
        type: EvidenceType;
        updated: string;
        value: { id: string; path: string } | string | undefined | Perpetrator;
      }>;
      report: {
        category: ReportCategory;
        created: string;
        id: string;
        status: string;
        updated: string;
      };
    };
    id: string;
    perpetrators: Array<{ external: boolean; id: string; name: string }>;
    report: string;
    status: string;
    user: string;
    relationshipToCompany?: number | null;
  };
  reportedCategory?: CategoryDisplayType;
  determinedCategory?: CategoryDisplayType;
  status: ReportStatus;
  source: keyof typeof REPORT_SOURCE;
  submissionMode: string | null;
  updatedAt: string;
  user: string;
  substantiation: ReportSubstantiation;
  phoneLineType?: PhoneLineType;
  miReportSource?: MiReportSource;
  submittedAt?: string;
}

export interface Perpetrator {
  id?: string;
  name: string;
  role?: string;
  department?: string;
  site?: string;
  external?: boolean;
}
export interface Reporter {
  id?: number;
  name: string;
  role: string;
  eid: string;
  email: string;
  relationshipToOrganisation?: string;
  contactEmail?: string;
  department?: string;
  country?: string;
  site?: string;
}

export const handleAxiosError = (
  error: AxiosError<{ errors: { message: string }[] }>,
): { message: string }[] => {
  console.groupCollapsed('Network Request Error');
  console.groupCollapsed('Traceback');
  console.trace();
  console.groupEnd();
  console.log('config:', error.config);
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx.
    console.log('data:', error.response.data);
    console.log('status:', error.response.status);
    console.log('headers:', error.response.headers);

    if (error.response.data.errors) {
      console.groupEnd();
      return error.response.data.errors;
    }
  } else if (error.request) {
    // The request was made but no response was received.
    console.log('request:', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error.
    console.log('message:', error.message);
  }
  console.groupEnd();

  return [];
};
